import { MED_MEMBERSHIP_FACADE } from "src/utils/medMembership";
import medContentJson from "./medContent.json";

export type MedFormat = "pill" | "injection";
export type MedFrequency = "daily" | "weekly";

export type MedClaim = {
  id: string;
  /**
   * Key into `translations.json`, so Smartling still translates the copy. The
   * string may use `{{medicationName}}` and inline markup such as `<strong>`.
   */
  i18nKey: string;
};

export const MED_OPT_IN_CLAIM_IDS = [
  "fdaApprovedGlp1s",
  "companionApp",
  "onlineCare",
] as const;
export type MedOptInClaimId = (typeof MED_OPT_IN_CLAIM_IDS)[number];

export type MedOptInContent = {
  claims: (MedClaim & { id: MedOptInClaimId })[];
  /** `translations.json` keys, rendered in order as footnote paragraphs. */
  disclaimers: string[];
};

export type MedContent = {
  /** The one name used for this medication in checkout and the survey. */
  nameI18nKey: string;
  /** Absent for the generic `default` entry, which is not a specific drug. */
  ingredientI18nKey?: string;
  format?: MedFormat;
  frequency?: MedFrequency;
  survey?: {
    showInComparison: boolean;
  };
  checkout: {
    claims: MedClaim[];
  };
  /** Optional per facade; falls back to the `medication-default` entry. */
  medOptIn?: MedOptInContent;
};

/** A real drug: everything except the generic `default` entry. */
export type SpecificMedContent = MedContent &
  Required<Pick<MedContent, "ingredientI18nKey" | "format" | "frequency">>;

/** `medication-<facade>`, one per `MED_MEMBERSHIP_FACADE` value. */
export type MedSlug = keyof typeof medContentJson;
/** Every slug except the generic `default`, i.e. real drugs with a format. */
export type SpecificMedSlug = Exclude<MedSlug, "medication-default">;

// Keys are stored without the `t:` marker that <Text> expects, so the JSON
// stays usable by `i18n.t` as well.
const MED_CONTENT = medContentJson as Record<MedSlug, MedContent>;

export const MED_FREQUENCY_I18N_KEYS: Record<MedFrequency, string> = {
  daily: "medContent:frequencies:onceDaily",
  weekly: "medContent:frequencies:onceWeekly",
};

export const MED_SLUGS = Object.keys(MED_CONTENT) as MedSlug[];

export const getMedSlug = (facade: MED_MEMBERSHIP_FACADE) =>
  `medication-${facade}` as MedSlug;

export const getMedContentBySlug = (slug: MedSlug): MedContent =>
  MED_CONTENT[slug];

// The catalog test asserts every non-default entry has these fields.
export const getSpecificMedContent = (
  slug: SpecificMedSlug
): SpecificMedContent => MED_CONTENT[slug] as SpecificMedContent;

export const getMedContent = (
  facade: MED_MEMBERSHIP_FACADE | undefined
): MedContent | undefined =>
  facade ? MED_CONTENT[getMedSlug(facade)] : undefined;

export const getMedSlugsByFormat = (format: MedFormat): SpecificMedSlug[] =>
  MED_SLUGS.filter(
    (slug): slug is SpecificMedSlug => MED_CONTENT[slug].format === format
  );

// The catalog test asserts the default entry has opt-in content.
const DEFAULT_MED_OPT_IN = MED_CONTENT["medication-default"]
  .medOptIn as MedOptInContent;

export const getMedOptInContent = (
  facade: MED_MEMBERSHIP_FACADE | undefined
): MedOptInContent => getMedContent(facade)?.medOptIn ?? DEFAULT_MED_OPT_IN;
